import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, Target, Milestone, Building2, History } from "lucide-react";
import api from "../../lib/api";
import { DepartmentSearch } from "./DepartmentSearch";

const GOALS = [["become", "Become a firefighter"], ["in_service", "I'm in fire/EMS — moving to a career fire job"], ["promotional", "Preparing for a promotional position"]];
const STAGES = [["starting", "Just starting"], ["certs", "Building certifications & experience"], ["applying", "Actively applying"], ["in_service", "Working in the fire service"]];
const cls = "text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white w-full";

const ACCENT = {
  goal: { bar: "border-l-primary", chip: "bg-primary/10 text-primary", Icon: Target },
  stage: { bar: "border-l-amber-500", chip: "bg-amber-100 text-amber-800", Icon: Milestone },
  target: { bar: "border-l-emerald-600", chip: "bg-emerald-100 text-emerald-800", Icon: Building2 },
};

function Field({ label, value, testid, accent, editing, onEdit, onCancel, onSave, children }) {
  const a = ACCENT[accent];
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${a.bar} rounded-xl px-4 py-3 relative`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <p className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 ${a.chip}`}><a.Icon className="w-3 h-3" /> {label}</p>
        {!editing && <button type="button" onClick={onEdit} className="text-slate-400 hover:text-primary p-0.5" aria-label={`Edit ${label}`} data-testid={`${testid}-edit`}><Pencil className="w-3.5 h-3.5" /></button>}
      </div>
      {!editing ? <p className="text-sm font-semibold text-slate-900 mt-0.5 leading-snug" data-testid={`${testid}-value`}>{value}</p> : (
        <div className="mt-1.5 space-y-2" data-testid={`${testid}-editor`}>
          {children}
          <div className="flex gap-1.5">
            <button type="button" onClick={onSave} className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-900 text-white rounded-md px-2.5 py-1" data-testid={`${testid}-save`}><Check className="w-3 h-3" /> Save</button>
            <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-2 py-1" data-testid={`${testid}-cancel`}><X className="w-3 h-3" /> Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SummaryStrip({ data, onChange, onEditAll }) {
  const { summary, profile, target } = data;
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [targetSel, setTargetSel] = useState(null);
  const start = (k) => {
    setDraft({ goal: profile.goal || "become", stage: profile.stage || "starting", county: profile.county || "", state: profile.state || "", improve: profile.improve || [], target_department_id: target?.id || profile.target_department_id || "", target_name: target && !target.id ? target.name : "", no_department: !!profile.no_department });
    setTargetSel(target ? { id: target.id, name: target.name, city: target.city, state: target.state, name_only: !target.id } : null);
    setEditing(k);
  };
  const save = async () => {
    try {
      const body = { ...profile, city: "", ...draft, target_department_id: draft.target_department_id || null };
      const { data: r } = await api.put("/roadmap/profile", body);
      const targetChanged = editing === "target" && (r.target?.id || r.target?.name || null) !== (target?.id || target?.name || null);
      onChange(r); setEditing(null);
      toast.success(targetChanged ? "Target changed. Your general prep stays done; application, board and hiring milestones now track the new department — the old one's progress is kept under Previous targets." : "Updated — your road map and recommendations refreshed.", { duration: targetChanged ? 7000 : 4000 });
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Could not save."); }
  };
  const common = (k) => ({ editing: editing === k, onEdit: () => start(k), onCancel: () => setEditing(null), onSave: save });
  return (
    <div className="mb-4" data-testid="roadmap-summary">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Goal" value={summary.goal} testid="summary-goal" accent="goal" {...common("goal")}>
          <select value={draft.goal} onChange={(e) => setDraft({ ...draft, goal: e.target.value })} className={cls} data-testid="summary-goal-input">{GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </Field>
        <Field label="Stage" value={summary.stage} testid="summary-stage" accent="stage" {...common("stage")}>
          <select value={draft.stage} onChange={(e) => setDraft({ ...draft, stage: e.target.value })} className={cls} data-testid="summary-stage-input">{STAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </Field>
        <Field label="Target department" value={summary.target_name ? <>{summary.target_name}{target?.city || target?.state ? <span className="block text-xs font-normal text-slate-500">{[target.city, target.state].filter(Boolean).join(", ")}</span> : null}</> : "Not chosen yet"} testid="summary-target" accent="target" {...common("target")}>
          <DepartmentSearch testid="summary-target-search" compact state={profile.state} county={profile.county} value={targetSel} noDepartment={draft.no_department}
            onSelect={(d) => { setTargetSel(d); setDraft({ ...draft, target_department_id: d.id || "", target_name: d.id ? "" : d.name, no_department: false }); }}
            onClear={() => { setTargetSel(null); setDraft({ ...draft, target_department_id: "", target_name: "" }); }}
            onNone={() => { setTargetSel(null); setDraft({ ...draft, no_department: !draft.no_department, target_department_id: "", target_name: "" }); }} />
        </Field>
      </div>
      {data.previous_targets?.length > 0 && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="roadmap-previous-targets">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Previous targets</p>
          <p className="text-xs text-slate-500 mt-0.5">Application, board and hiring progress you made for these departments is saved — switch back and it returns.</p>
          <ul className="mt-2 divide-y divide-slate-100">
            {data.previous_targets.map((t) => (
              <li key={t.key} className="py-1.5 flex items-center justify-between gap-3 text-sm" data-testid={`roadmap-previous-target-${t.key}`}>
                <span className="text-slate-800 truncate">{t.name}</span>
                <span className="text-xs text-slate-500 shrink-0"><b className="text-slate-800">{t.done}</b> of {t.total} department steps done{t.saved_at ? ` · left ${new Date(t.saved_at).toLocaleDateString()}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-2 flex justify-end" data-testid="summary-actions">
        <button type="button" onClick={onEditAll} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-primary border border-slate-200 rounded-full px-3 py-1.5 bg-white" data-testid="roadmap-edit-evaluation"><Pencil className="w-3 h-3" /> Edit all answers</button>
      </div>
    </div>
  );
}
